/**
 * User Detail Page
 *
 * Mobile-first detail view for a single user.
 */

import { useParams, useNavigate, Link } from 'react-router-dom'
import {
  ArrowLeft,
  Pencil,
  User as UserIcon,
  Mail,
  Shield,
  ShieldAlert,
  BookOpen,
} from 'lucide-react'

import { useUser } from '@/hooks/useUsers'
import { t } from '@/i18n'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Badge } from '@/components/ui/badge'
import { UserAvatar } from '@/components/AvatarSelector'

const getRoleInfo = (role: string) => {
  switch (role) {
    case 'admin':
      return {
        icon: ShieldAlert,
        label: () => t('users.admin'),
        color: 'text-red-500',
        bgColor: 'bg-red-100',
      }
    case 'accountant':
      return {
        icon: BookOpen,
        label: () => t('users.accountant'),
        color: 'text-blue-500',
        bgColor: 'bg-blue-100',
      }
    default:
      return {
        icon: Shield,
        label: () => t('users.seller'),
        color: 'text-gray-500',
        bgColor: 'bg-gray-100',
      }
  }
}

export function UserDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const userId = id && !isNaN(Number(id)) ? Number(id) : 0
  const { data: user, isLoading, error } = useUser(userId)

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center gap-3">
          <Skeleton className="h-10 w-10" />
          <Skeleton className="h-8 w-48" />
        </div>
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  if (error || !user) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <UserIcon className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-lg font-medium mb-2">{t('users.userNotFound')}</p>
        <p className="text-sm text-muted-foreground mb-4">
          {error ? t('users.errorLoadingUser') : t('users.userDoesNotExist')}
        </p>
        <Button variant="outline" onClick={() => navigate('/users')}>
          <ArrowLeft className="h-4 w-4 mr-2" />
          {t('users.backToUsers')}
        </Button>
      </div>
    )
  }

  const roleInfo = getRoleInfo(user.role)
  const RoleIcon = roleInfo.icon

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="h-10 w-10 shrink-0"
          onClick={() => navigate('/users')}
        >
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <UserAvatar
          avatarId={user.avatar}
          name={user.full_name}
          size="lg"
        />
        <div className="flex-1 min-w-0">
          <h1 className="text-xl md:text-2xl font-bold truncate">{user.full_name}</h1>
          <p className="text-sm text-muted-foreground">@{user.username}</p>
        </div>
        <Badge variant={user.is_active ? 'success' : 'secondary'}>
          {user.is_active ? t('common.active') : t('common.inactive')}
        </Badge>
      </div>

      {/* Quick Actions */}
      <div className="flex gap-2">
        <Button asChild className="flex-1 h-12">
          <Link to={`/users/${id}/edit`}>
            <Pencil className="h-4 w-4 mr-2" />
            {t('common.edit')}
          </Link>
        </Button>
      </div>

      {/* Role Card */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('users.role')}</CardTitle>
        </CardHeader>
        <CardContent>
          <div className={`flex items-center gap-3 p-4 rounded-lg ${roleInfo.bgColor}`}>
            <RoleIcon className={`h-8 w-8 ${roleInfo.color}`} />
            <div>
              <p className="font-medium text-lg">{roleInfo.label()}</p>
              <p className="text-sm text-muted-foreground">{t(`users.roleDescription.${user.role}`)}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Contact Info */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">{t('users.contactInfo')}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {user.email ? (
            <a
              href={`mailto:${user.email}`}
              className="flex items-center gap-3 p-3 rounded-lg bg-muted/50 active:bg-muted"
            >
              <Mail className="h-5 w-5 text-muted-foreground" />
              <span className="truncate">{user.email}</span>
            </a>
          ) : (
            <div className="flex items-center gap-3 p-3 rounded-lg bg-muted/50">
              <Mail className="h-5 w-5 text-muted-foreground" />
              <span className="text-muted-foreground">{t('users.noEmail')}</span>
            </div>
          )}
        </CardContent>
      </Card>

    </div>
  )
}
